import { memo, useState } from 'react'
import { Permission, PermissionGrant } from '../../../../../../../graphql'
import {
	EuiButtonIcon,
	EuiContextMenuItem,
	EuiContextMenuPanel,
	EuiPopover,
} from '@equipmentshare/ds2'

import { More2FillIcon } from '@equipmentshare/ds2-icons'

import { checkPermissions } from 'components/PermissionsCheck/helpers'
import { InventoryLocationWithLabel } from 'hooks/inventory-location-search'

export type ActionsProps = {
	inventoryLocation: InventoryLocationWithLabel,
  onEdit: ( inventoryLocation: InventoryLocationWithLabel ) => ( e: React.MouseEvent ) => void,
	onMove: ( inventoryLocation: InventoryLocationWithLabel ) => ( e: React.MouseEvent ) => void,
	onDelete: ( inventoryLocation: InventoryLocationWithLabel ) => ( e: React.MouseEvent ) => void,
	onSetDefault: ( inventoryLocation: InventoryLocationWithLabel ) => ( e: React.MouseEvent ) => void,
	onWarehouseSettings: ( inventoryLocation: InventoryLocationWithLabel ) => ( e: React.MouseEvent ) => void,
  userPermissions: PermissionGrant[],
  totalNumberOfInventories: number,
}

// The context menu (and all of its items) is only rendered while the popover is open.
// Mounting one of these for every store row up-front is what made the Stores page slow,
// so it is split out and lazily mounted by the Actions below.
export function ActionsMenu( { inventoryLocation, onEdit, onMove, onDelete, onSetDefault, onWarehouseSettings, userPermissions, totalNumberOfInventories }: ActionsProps ) {
	const canEdit = checkPermissions( userPermissions, [ Permission.CanEditStoreByStoreId ] )
	const canMove = checkPermissions( userPermissions, [ Permission.CanCreateTopLevelStore ] )
	const canArchive = checkPermissions( userPermissions, [ Permission.CanArchiveStoreByStoreId ] )

	return (
		<EuiContextMenuPanel
			items={[
				<EuiContextMenuItem
					key="edit"
					disabled={!canEdit}
					onClick={onEdit( inventoryLocation )}
				>
					Edit name
				</EuiContextMenuItem>,
				<EuiContextMenuItem
					key="move"
					disabled={!canMove || inventoryLocation.defaultLocation}
					onClick={onMove( inventoryLocation )}
				>
					Move to branch
				</EuiContextMenuItem>,
				<EuiContextMenuItem
					key="setDefault"
					disabled={!canEdit || inventoryLocation.defaultLocation}
					onClick={onSetDefault( inventoryLocation )}
				>
					Set as main inventory
				</EuiContextMenuItem>,
				<EuiContextMenuItem
					key="warehouseSettings"
					disabled={!canEdit}
					onClick={onWarehouseSettings( inventoryLocation )}
				>
					Warehouse settings
				</EuiContextMenuItem>,
				<EuiContextMenuItem
					key="delete"
					// if this is the default inventory location, disable delete.
					// however, if this is the remaining inventory location in a branch, allow deletion
					disabled={!canArchive || ( totalNumberOfInventories > 1 && inventoryLocation.defaultLocation )}
					onClick={onDelete( inventoryLocation )}
				>
					Delete location
				</EuiContextMenuItem>
			]}
		/>
	)
}

function Actions( { inventoryLocation, onEdit, onMove, onDelete, onSetDefault, onWarehouseSettings, userPermissions, totalNumberOfInventories }: ActionsProps ) {
	const [ isPopoverOpen, setIsPopoverOpen ] = useState( false )

	const togglePopover = () => setIsPopoverOpen( ( prev ) => !prev )
	const closePopover = () => setIsPopoverOpen( false )

	return (
		<EuiPopover
			button={
				<EuiButtonIcon
					aria-label="Actions"
					iconType={More2FillIcon}
					onClick={togglePopover}
					color="text"
					size="m"
				/>
			}
			closePopover={closePopover}
			isOpen={isPopoverOpen}
		>
			{ isPopoverOpen && (
				<ActionsMenu
					inventoryLocation={inventoryLocation}
					onEdit={onEdit}
					onMove={onMove}
					onDelete={onDelete}
					onSetDefault={onSetDefault}
					onWarehouseSettings={onWarehouseSettings}
					userPermissions={userPermissions}
					totalNumberOfInventories={totalNumberOfInventories}
				/>
			) }
		</EuiPopover>
	)
}

const MemoizedActions = memo( Actions )
MemoizedActions.displayName = 'Actions'

export default MemoizedActions
